import { useState } from "react";
import {
  CommonCodeStatus,
  CreateCommonCodeDto,
  useCreateCommonCodeAsAdminMutation,
  useGetCommonCodeAsAdminQuery,
  useGetCommonCodeGroupsForSelectionAsAdminQuery,
  useUpdateCommonCodeAsAdminMutation,
} from "../../../lib/apollo/graphql/generated";
import Modal, { ModalProps } from "../../common/components/Modal";
import LabeledInput from "../../common/components/LabeledInput";
import SelectBox from "../../common/components/SelectBox";
import BasicButton from "../../common/components/BasicButton";

interface UpdateCodeModalProps extends ModalProps {
  codeId: string | null;
}

export default function UpdateCodeModal({
  codeId,
  open,
  onClose,
}: UpdateCodeModalProps) {
  const [formValues, setFormValues] = useState({
    name: "",
    code: "",
    commonCodeGroupId: "",
    status: CommonCodeStatus.Pending,
  });

  const onFormChange = <K extends keyof CreateCommonCodeDto>(
    key: K,
    value: CreateCommonCodeDto[K]
  ) => {
    setFormValues((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const isCreate = !codeId;

  const { loading } = useGetCommonCodeAsAdminQuery({
    skip: !codeId,
    variables: {
      commonCodeId: codeId!,
    },
    onCompleted: (res) => {
      const code = res?.getCommonCodeAsAdmin;

      setFormValues({
        name: code.name,
        code: code.code,
        status: code.status,
        commonCodeGroupId: code.commonCodeGroupId,
      });
    },
  });

  const { data: commonCodeGroups, loading: isCommonCodeGroupsLoading } =
    useGetCommonCodeGroupsForSelectionAsAdminQuery({
      variables: {
        first: 100,
      },
    });

  const options =
    commonCodeGroups?.getCommonCodeGroupsAsAdmin.edges.map((group) => ({
      label: group.name,
      value: group.id,
    })) || [];

  const [updateCode, { loading: isUpdating }] =
    useUpdateCommonCodeAsAdminMutation({
      onCompleted: () => {
        window.alert("코드를 수정되었습니다");
        onClose();
      },
      refetchQueries: ["GetCommonCodesAsAdmin", "GetCommonCodeAsAdmin"],
    });

  const [createCode, { loading: isCreating }] =
    useCreateCommonCodeAsAdminMutation({
      onCompleted: () => {
        window.alert("코드가 등록되었습니다");
        onClose();
      },
      refetchQueries: ["GetCommonCodesAsAdmin", "GetCommonCodeAsAdmin"],
    });

  const isSubmitDisabled =
    isCreating ||
    isUpdating ||
    loading ||
    isCommonCodeGroupsLoading ||
    !formValues.name ||
    !formValues.code ||
    !formValues.commonCodeGroupId;

  const onSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();

    if (isSubmitDisabled) {
      return;
    }

    if (isCreate) {
      createCode({
        variables: {
          createCommonCodeInput: formValues,
        },
      });
    } else {
      updateCode({
        variables: {
          updateCommonCodeInput: {
            ...formValues,
            id: codeId,
          },
        },
      });
    }
  };

  return (
    <Modal
      title={`코드 ${isCreate ? "등록" : "수정"}`}
      open={open}
      onClose={onClose}
      height="700px"
    >
      <form onSubmit={onSubmit} className="flex h-full w-full flex-col gap-5">
        <SelectBox
          label="그룹"
          value={formValues.commonCodeGroupId}
          onSelect={(value) => onFormChange("commonCodeGroupId", value)}
          options={options}
          className="h-[50px]"
        />
        <LabeledInput
          label="코드"
          value={formValues.code}
          onChange={(event) => onFormChange("code", event.target.value.trim())}
        />
        <LabeledInput
          label="코드명"
          value={formValues.name}
          onChange={(event) => onFormChange("name", event.target.value)}
        />
        <SelectBox
          label="상태"
          value={formValues.status}
          onSelect={(value) => onFormChange("status", value)}
          options={[
            { label: "활성", value: CommonCodeStatus.Active },
            { label: "대기", value: CommonCodeStatus.Pending },
            { label: "비활성", value: CommonCodeStatus.Inactive },
          ]}
          className="h-[50px]"
        />
        <BasicButton
          text={isCreate ? "등록" : "수정"}
          className="mt-auto w-1/2 self-center"
          type="submit"
          loading={isCreate ? isCreating : isUpdating}
          disabled={isSubmitDisabled}
          onClick={() => onSubmit()}
        />
      </form>
    </Modal>
  );
}
